import React, { useEffect, useState, Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import qs from 'qs';
import i18n from 'i18n-js';
import {
  Box,
  List,
  ListItem,
  Typography,
  IconButton,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import Can from 'components/Can';
import { Card } from 'components/UI';
import Loading from 'components/Loading';
import { uiActions } from 'state/modules/UI';
import StarIcon from '@mui/icons-material/Star';
import DeleteIcon from '@material-ui/icons/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PaymentActions } from 'state/modules/payment';
import { VerifyPaymentMethodModal } from 'components/Modals';
import { FETCH_STATE } from 'constants';
import { useHistory, useLocation } from 'react-router-dom';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { PaymentNumberStripe, PaymentIconStripe } from '../PaymentComponentsStripe';
import { useStyles, listTextStyles, customCardStyles } from './PaymentMethodsWidgetStripe.styled';

const PaymentMethodsStripe = ({ accountId }) => {
  const classes = useStyles();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();

  const [params] = useState({ page: 1, limit: 5 });
  const [openVerifyPaymentMethod, setOpenVerifyPaymentMethod] = useState(false);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);

  const query = qs.parse(window.location.search.substring(1));
  const location = useLocation();
  const history = useHistory();

  const dispatch = useDispatch();

  const paymentMethods = useSelector((state) => state.payment.paymentMethods);
  const fetchPaymentMethods = useSelector((state) => state.payment.fetchPaymentMethods);
  const fetchAddPaymentMethod = useSelector((state) => state.payment.fetchAddPaymentMethod);
  const fetchUpdatePaymentMethod = useSelector((state) => state.payment.fetchUpdatePaymentMethod);
  const fetchDeletePaymentMethod = useSelector((state) => state.payment.fetchDeletePaymentMethod);
  const fetchCheckoutSession = useSelector((state) => state.payment.fetchCheckoutSession);

  const loading =
    isNaN(accountId) ||
    fetchPaymentMethods === FETCH_STATE.LOADING ||
    fetchAddPaymentMethod === FETCH_STATE.LOADING ||
    fetchUpdatePaymentMethod === FETCH_STATE.LOADING ||
    fetchDeletePaymentMethod === FETCH_STATE.LOADING ||
    fetchCheckoutSession === FETCH_STATE.LOADING;

  const refetch =
    fetchAddPaymentMethod === FETCH_STATE.DONE ||
    fetchUpdatePaymentMethod === FETCH_STATE.DONE ||
    fetchDeletePaymentMethod === FETCH_STATE.DONE;

  const removeParams = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('success');
    queryParams.delete('token');
    queryParams.delete('refId');
    queryParams.delete('stripe_status');
    history.replace({ search: queryParams.toString() });
  };

  useEffect(() => {
    if (query.success === 'false' || query.stripe_status === 'cancel') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('payment_method.setup_not_completed'),
        })
      );
      removeParams();
    }
    if (query.stripe_status === 'success') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('payment_method.added'),
        })
      );
      removeParams();
      getPaymentMethods(params);
    }
  }, [query.success, query.stripe_status, dispatch]); // eslint-disable-line

  const getPaymentMethods = useCallback(
    (params) => {
      const payload = { ...params };
      if (accountId) {
        payload.account_id = accountId;
      }
      dispatch(PaymentActions.fetchPaymentMethods(payload));
    },
    [accountId, dispatch]
  );

  const handleVerifyPaymentMethodModalOpen = () => {
    setCurrentPaymentId(null);
    setOpenVerifyPaymentMethod((prevState) => !prevState);
  };

  useEffect(() => {
    if (accountId) {
      getPaymentMethods(params);
    }
  }, [accountId]); // eslint-disable-line

  useEffect(() => {
    if (refetch) {
      getPaymentMethods(params);
    }
  }, [params, getPaymentMethods, refetch]);

  const handleManageInStripe = () => {
    const successUrl = `${window.location.origin}${window.location.pathname}?stripe_status=success`;
    const cancelUrl = `${window.location.origin}${window.location.pathname}?stripe_status=cancel`;
    dispatch(
      PaymentActions.fetchCheckoutSession({
        success_url: successUrl,
        cancel_url: cancelUrl,
        broker_type: 'STP',
      })
    );
  };

  const onDeleteClick = (item) => {
    if (item?.id) {
      dispatch(PaymentActions.fetchDeletePaymentMethod(item?.id));
    }
  };

  const onChangeDefault = (item) => {
    if (item?.id) {
      dispatch(PaymentActions.fetchUpdatePaymentMethod(item?.id, { default: !item.default }));
    }
  };

  const isStripePaymentEnabled = useFeatureIsOn('feat.backend.payment-method');

  const verifiedPaymentMethodsCount =
    paymentMethods?.filter((paymentMethod) => {
      const paymentData = {
        ...(paymentMethod?.card || paymentMethod?.ach || {}),
        ...paymentMethod,
      };
      const status = String(paymentData?.status || '').toUpperCase();
      return status === 'VERIFIED';
    })?.length || 0;

  return (
    <Card
      menuItems={[]}
      title={i18n.t('payment_method.stripe_title')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
      onRefresh={() => getPaymentMethods(params)}
    >
      <Box className={classes.cardContentPadding}>
        {loading || refetch ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <>
            {paymentMethods?.length > 0 ? (
              <List data-cy="payment_methods_added">
                {paymentMethods?.map((val, index) => {
                  const paymentData = { ...(val?.card || val?.ach || {}), ...val };
                  const isVerifying =
                    paymentData?.status === 'processing' ||
                    paymentData?.status === 'verifying' ||
                    (paymentData?.payment_type === 'ach' && paymentData?.status === 'NEW');
                  const isFailed = paymentData?.status === 'FAILED';

                  return (
                    <Fragment key={index}>
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <PaymentIconStripe type={val?.payment_type} />
                        </ListItemIcon>
                        <ListItemText
                          classes={listClasses}
                          primary={
                            <PaymentNumberStripe
                              data={paymentData}
                              setCurrentPaymentId={setCurrentPaymentId}
                              setOpenVerifyPaymentMethod={setOpenVerifyPaymentMethod}
                            />
                          }
                        />
                        {!isFailed && !isVerifying && verifiedPaymentMethodsCount > 1 && (
                          <Box fontSize={13} style={{ maxWidth: '2.5rem' }}>
                            <Can I="update" on="payment_method">
                              <IconButton onClick={() => onChangeDefault(val)}>
                                <DefaultIcon isDefault={val?.default} />
                              </IconButton>
                            </Can>
                            <Can I="delete" on="payment_method">
                              <IconButton
                                data-cy="btn-delete-payment-method"
                                disabled={fetchDeletePaymentMethod === FETCH_STATE.LOADING}
                                onClick={() => onDeleteClick(val)}
                              >
                                <DeleteIcon
                                  style={{ color: '#8E8E93', fontSize: 16, cursor: 'pointer' }}
                                />
                              </IconButton>
                            </Can>
                          </Box>
                        )}
                      </ListItem>
                    </Fragment>
                  );
                })}
              </List>
            ) : null}
            {isStripePaymentEnabled && (
              <Can I="checkout_session" on="payment_method">
                <Box mb={paymentMethods?.length ? 2 : 1}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<OpenInNewIcon />}
                    onClick={handleManageInStripe}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      py: 1,
                      px: 2,
                      margin: '20px auto 0',
                      display: 'flex',
                    }}
                  >
                    {i18n.t('payment_method.manage_in_stripe')}
                  </Button>
                </Box>
              </Can>
            )}
          </>
        )}
      </Box>
      {openVerifyPaymentMethod ? (
        <VerifyPaymentMethodModal
          paymentId={currentPaymentId}
          open={true}
          handleClick={handleVerifyPaymentMethodModalOpen}
          accountId={accountId}
        />
      ) : null}

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
        open={fetchCheckoutSession === FETCH_STATE.LOADING}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">{i18n.t('payment_method.redirecting_to_stripe')}</Typography>
        <Typography variant="body2">{i18n.t('payment_method.security_note_pci')}</Typography>
      </Backdrop>
    </Card>
  );
};

const DefaultIcon = ({ isDefault }) => {
  return isDefault ? (
    <StarIcon style={{ color: '#FFB027', fontSize: 16, cursor: 'pointer' }} />
  ) : (
    <StarBorderIcon style={{ fontSize: 16, cursor: 'pointer' }} />
  );
};

DefaultIcon.propTypes = {
  isDefault: PropTypes.bool,
};

PaymentMethodsStripe.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export { PaymentMethodsStripe };
